// Shopify Storefront API client for Vesta Workwear
const SHOPIFY_STORE_DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;
const STOREFRONT_ACCESS_TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const API_VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION;

const STOREFRONT_URL = `https://${SHOPIFY_STORE_DOMAIN}/api/${API_VERSION}/graphql.json`;

export const shopifyFetch = async (query, variables = {}) => {
  const response = await fetch(STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  const json = await response.json();

  if (json.errors) {
    throw new Error(json.errors[0]?.message || 'Shopify API Error');
  }

  return json.data;
};

// Fetch all products
export const getProducts = async () => {
  const query = `
    query getProducts {
      products(first: 20) {
        edges {
          node {
            id
            title
            handle
            description
            descriptionHtml
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 5) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
            options {
              name
              values
            }
            availableForSale
            tags
          }
        }
      }
    }
  `;

  const data = await shopifyFetch(query);
  return data.products.edges.map(({ node }) => transformProduct(node));
};

// Fetch single product by handle
export const getProductByHandle = async (handle) => {
  const query = `
    query getProductByHandle($handle: String!) {
      productByHandle(handle: $handle) {
        id
        title
        handle
        description
        descriptionHtml
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        images(first: 10) {
          edges {
            node {
              url
              altText
            }
          }
        }
        variants(first: 20) {
          edges {
            node {
              id
              title
              price {
                amount
                currencyCode
              }
              availableForSale
              selectedOptions {
                name
                value
              }
            }
          }
        }
        options {
          name
          values
        }
        availableForSale
        tags
      }
    }
  `;

  const data = await shopifyFetch(query, { handle });
  return data.productByHandle ? transformProduct(data.productByHandle) : null;
};

// Create checkout (cart) - Using Shopify's Cart API
export const createCheckout = async (lineItems) => {
  const query = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
          lines(first: 10) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    title
                  }
                }
              }
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const input = {
    lines: lineItems.map(item => ({
      merchandiseId: item.variantId,
      quantity: item.quantity,
    })),
  };

  const data = await shopifyFetch(query, { input });
  
  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors[0].message);
  }

  // Get the checkout URL and ensure it uses myshopify.com domain
  // This is needed because the custom domain points to our React app, not Shopify
  let checkoutUrl = data.cartCreate.cart.checkoutUrl;
  
  // Force use of myshopify.com domain for checkout
  if (checkoutUrl) {
    checkoutUrl = checkoutUrl.replace('vestaworkwear.com.au', 'vesta-workwear.myshopify.com');
  }

  // Return in the same format as before, with webUrl for compatibility
  return {
    id: data.cartCreate.cart.id,
    webUrl: checkoutUrl,
    lineItems: data.cartCreate.cart.lines,
  };
};

// Add items to existing checkout
export const addToCheckout = async (checkoutId, lineItems) => {
  const query = `
    mutation checkoutLineItemsAdd($checkoutId: ID!, $lineItems: [CheckoutLineItemInput!]!) {
      checkoutLineItemsAdd(checkoutId: $checkoutId, lineItems: $lineItems) {
        checkout {
          id
          webUrl
          lineItems(first: 20) {
            edges {
              node {
                id
                title
                quantity
                variant {
                  id
                  price {
                    amount
                  }
                }
              }
            }
          }
          subtotalPrice {
            amount
            currencyCode
          }
          totalPrice {
            amount
            currencyCode
          }
        }
        checkoutUserErrors {
          code
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch(query, {
    checkoutId,
    lineItems: lineItems.map(item => ({
      variantId: item.variantId,
      quantity: item.quantity,
    })),
  });

  if (data.checkoutLineItemsAdd.checkoutUserErrors.length > 0) {
    throw new Error(data.checkoutLineItemsAdd.checkoutUserErrors[0].message);
  }

  return data.checkoutLineItemsAdd.checkout;
};

// Transform Shopify product to our format
const transformProduct = (shopifyProduct) => {
  const price = parseFloat(shopifyProduct.priceRange.minVariantPrice.amount);
  const currency = shopifyProduct.priceRange.minVariantPrice.currencyCode;
  
  // Get size options
  const sizeOption = shopifyProduct.options.find(opt => 
    opt.name.toLowerCase() === 'size'
  );
  const sizes = sizeOption ? sizeOption.values : ['One Size'];

  // Get color options
  const colorOption = shopifyProduct.options.find(opt => 
    opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour'
  );
  
  const colors = colorOption 
    ? colorOption.values.map(color => ({
        name: color,
        hex: getColorHex(color),
        available: true,
      }))
    : null;

  return {
    id: shopifyProduct.id,
    shopifyId: shopifyProduct.id,
    name: shopifyProduct.title,
    slug: shopifyProduct.handle,
    tagline: shopifyProduct.description?.substring(0, 100) || '',
    price: price,
    currency: currency,
    inStock: shopifyProduct.availableForSale,
    featured: shopifyProduct.tags.includes('featured'),
    images: shopifyProduct.images.edges.map(({ node }) => node.url),
    sizes: sizes,
    colors: colors,
    shortDescription: shopifyProduct.description,
    descriptionHtml: shopifyProduct.descriptionHtml,
    variants: shopifyProduct.variants.edges.map(({ node }) => ({
      id: node.id,
      title: node.title,
      price: parseFloat(node.price.amount),
      available: node.availableForSale,
      options: node.selectedOptions,
    })),
    options: shopifyProduct.options,
  };
};

// Helper to map color names to hex codes
const getColorHex = (colorName) => {
  const colorMap = {
    'teal': '#004D51',
    'charcoal': '#545759',
    'grey': '#545759',
    'gray': '#545759',
    'orange': '#DB5A14',
    'black': '#000000',
    'white': '#FFFFFF',
  };
  return colorMap[colorName.toLowerCase()] || '#CCCCCC';
};

// Get variant ID based on selected options
export const getVariantId = (product, selectedOptions) => {
  const variant = product.variants.find(v => {
    return v.options.every(opt => {
      const selectedValue = selectedOptions[opt.name.toLowerCase()];
      return !selectedValue || opt.value === selectedValue;
    });
  });
  return variant?.id || product.variants[0]?.id;
};
